<script setup lang="ts">
import CurrencySwitcher from '@/components/billing/CurrencySwitcher.vue'
import PlanCard from '@/components/business/upgrade/PlanCard.vue'
import ScalePlanCard from '@/components/business/upgrade/ScalePlanCard.vue'
import SkrSwitch from '@/components/inputs/SkrSwitch.vue'

const props = withDefaults(
  defineProps<{
    showCurrencySwitcher?: boolean
    plans?: PricePlan[]
    currency?: Currency
    cycle?: BillingCycle
    isPlanUpgrade?: boolean
  }>(),
  {
    showCurrencySwitcher: false,
    plans: () => [],
    isPlanUpgrade: false,
  }
)

const emit = defineEmits<{
  choose: [payload: { plan?: PricePlan; currency?: Currency; cycle?: BillingCycle }]
}>()

const businessStore = useBusinessStore()
const paymentStore = usePaymentStore()

const getPlanRank = (planName: PricePlanName | '') => {
  return planName ? paymentStore.planHierarchy.indexOf(planName) : -1
}

const { currentPricePlanName: currentPlanName, isCancelling, isLocked, isTrialing } = storeToRefs(businessStore)

const displayedPlans = computed(() => {
  return props.plans
    .filter(plan => {
      if (isCancelling.value) return false
      if (isLocked.value || isTrialing.value) return plan
      return getPlanRank(currentPlanName.value) <= getPlanRank(plan.name)
    })
    .sort((planA, planB) => getPlanRank(planA.name) - getPlanRank(planB.name))
})

// Every SCALE plan is unique, so we pass data limited to what we know
const scalePlan: Omit<PricePlan, 'yearly' | 'monthly'> = {
  name: 'Scale',
  id: 'SCALE',
  seatLimit: 50,
}

const selectedCurrency = computed({
  get(): Currency {
    return props.currency ?? 'EUR'
  },
  set(currency: Currency) {
    emit('choose', { currency })
  },
})

const billingCycle = computed({
  get(): BillingCycle {
    return props.cycle ?? 'yearly'
  },
  set(cycle: BillingCycle) {
    emit('choose', { cycle })
  },
})

const choosePlan = (plan: PricePlan) => {
  emit('choose', {
    plan,
    currency: selectedCurrency.value,
    cycle: billingCycle.value,
  })
}
</script>

<template>
  <div>
    <section v-if="!isPlanUpgrade" class="d-flex align-center justify-space-between mb-12 px-12 px-md-0">
      <div class="cycle-switch">
        <span :class="{ 'text-grey-dark': billingCycle === 'monthly' }">
          {{ $t('billing.billing_cycle.monthly') }}
        </span>
        <skr-switch
          v-model="billingCycle"
          class="mt-0 pt-0 mx-2"
          color="green"
          true-value="yearly"
          false-value="monthly"
        />
        <span :class="{ 'text-success': billingCycle === 'yearly' }">{{ $t('billing.billing_cycle.yearly') }}</span>
        <div v-if="$vuetify.display.smAndUp" class="discount">
          <nuxt-img src="/img/highlight-arrow.svg" alt="" />
          <span>{{ $t('billing.billing_cycle.yearly_discount') }}</span>
        </div>
      </div>
      <currency-switcher v-if="showCurrencySwitcher" v-model="selectedCurrency" />
    </section>
    <section class="plans">
      <plan-card
        v-for="plan in displayedPlans"
        :key="plan.id"
        :price-plan="plan"
        :currency="selectedCurrency"
        :billing-cycle="billingCycle"
        :is-current="plan.name === currentPlanName && !isLocked"
        @choose="choosePlan"
      />
      <scale-plan-card :price-plan="scalePlan" :is-current="currentPlanName === 'Scale'" />
    </section>
    <section class="mt-12">
      <div class="text-body-2">
        <div>
          <span class="superscript font-weight-semibold mr-1">1</span
          >{{ $t('business.plans.footnote.additional_signatures') }}
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.cycle-switch {
  color: $c-grey;
  position: relative;
  display: flex;
  align-items: center;
  font-weight: bold;

  .discount {
    position: absolute;
    left: 100%;
    bottom: 66%;
    display: flex;

    > span {
      color: $c-success;
      position: relative;
      top: 0.75rem;
      white-space: nowrap;
    }
  }
}

.plans {
  display: flex;
  gap: 24px;
  justify-content: center;
  flex-wrap: wrap;
}

.superscript {
  position: relative;
  bottom: 0.4em;
  font-size: 0.8em;
}
</style>
